import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

// Centred confirmation modal. Replaces window.confirm() with something
// brand-styled and theme-aware. Keep it minimal — title + body + two
// buttons. For anything richer (forms, multi-step), use SideDrawer or a
// bespoke modal.
//
// `tone` controls the confirm-button colour:
//   'primary' — Fusion green (default for safe actions)
//   'danger'  — red outline (irreversible / destructive)
//
// Escape cancels.
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  onConfirm,
  onCancel,
  // Optional third button rendered between Cancel and Confirm — { label, onClick }.
  // Use for a distinct secondary path (e.g. "Winner only" on the award dialog).
  extraAction = null,
}) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmClass = tone === 'danger'
    ? 'bg-transparent border border-[#E0290A]/50 text-[#E0290A] hover:bg-[#E0290A]/10'
    : 'bg-[#92D400] border border-[#92D400] text-[#131313] hover:bg-[#a4e810]';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-line rounded-xl w-full max-w-md p-7"
        style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="font-['Metropolis'] text-base font-bold text-ink mb-2">{title}</div>
        {body && <div className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed mb-6">{body}</div>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white border border-line rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors">
            {cancelLabel}
          </button>
          {extraAction && (
            <button
              type="button"
              onClick={extraAction.onClick}
              className="bg-white border border-line rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors">
              {extraAction.label}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase px-4 py-2 transition-colors ${confirmClass}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
